import React from 'react';
import { toast } from 'react-toastify';
import type Talk from 'talkjs';
import useGetChatHeaderAppointment from './useGetChatHeaderAppointment';
import useUser from '../global/useUser';
import useLoadingModal from '../global/useLoadingModal';
import { SAVE_CHAT_PHOTO_ACTION } from '../../constants/chatPhotos';
import {
  ChatPhotoSaveDestination,
  getChatAttachmentUrl,
  getChatPhotoFileName,
  getChatPhotoMimeType,
  isChatImageMessage,
} from '../../helpers/chats/chatPhotos';
import {
  getAppointmentIdFromConversation,
  getChatParticipants,
  getParticipantId,
} from '../../helpers/chats';
import { UserConversation } from '../../types/chats';
import { LoadedUser } from '../../interfaces';
import {
  appendAppointmentRefImage,
  invalidateAppointment,
  invalidateAppointments,
} from '../../services/appointments';
import {
  invalidateCustomerOverview,
  uploadCustomerFileFromUrl,
} from '../../services/customers';
import { uploadBlobToPath } from '../../services/global';
import { auth } from '../../firebase';
import {
  HEIC_UNSUPPORTED_MESSAGE,
  isHeicUnsupportedError,
  normalizeRemoteImage,
} from '../../utils/image';
import useCalendarSelectedAppointment from '../appointments/useCalendarSelectedAppointment';

function useSaveChatPhoto(conversation: UserConversation | null) {
  const { user } = useUser<LoadedUser>();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const appointmentContext = useGetChatHeaderAppointment(conversation);
  const {
    openedAppointment,
    setOpenedAppointment,
    setSelectedAppointmentUpdated,
  } = useCalendarSelectedAppointment();

  const destination = React.useMemo<ChatPhotoSaveDestination | null>(() => {
    if (!conversation || !user?.userId) return null;

    const appointmentId =
      appointmentContext.appointmentId ||
      getAppointmentIdFromConversation(conversation);

    if (appointmentId) {
      return {
        type: 'appointment',
        appointmentId,
      };
    }

    const { customerParticipant } = getChatParticipants(conversation, user);
    const customerId =
      appointmentContext.appointment?.customerId ||
      (customerParticipant
        ? getParticipantId(customerParticipant.userId)
        : null);

    if (!customerId) return null;
    return { type: 'customer', customerId };
  }, [
    appointmentContext.appointment?.customerId,
    appointmentContext.appointmentId,
    conversation,
    user,
  ]);

  const saveHandlerRef = React.useRef<
    ((event: Talk.MessageActionEvent) => Promise<void>) | null
  >(null);

  saveHandlerRef.current = async (event: Talk.MessageActionEvent) => {
    if (event.action !== SAVE_CHAT_PHOTO_ACTION) return;

    if (!isChatImageMessage(event.message)) {
      toast.error('Only images can be saved.');
      return;
    }

    const attachmentUrl = getChatAttachmentUrl(event.message);
    if (!attachmentUrl) {
      toast.error('Only images can be saved.');
      return;
    }

    if (appointmentContext.isEnabled && appointmentContext.isLoading) {
      toast.error('Still loading chat details. Try again in a moment.');
      return;
    }

    if (!destination) {
      toast.error('Unable to find an appointment or customer to save to.');
      return;
    }

    if (
      destination.type === 'appointment' &&
      !appointmentContext.appointment?.status
    ) {
      toast.error('Still loading chat details. Try again in a moment.');
      return;
    }

    const authServiceId = auth.currentUser?.uid;
    const businessId = user?.business?.id || conversation?.custom?.business_id;
    if (!authServiceId || !businessId) {
      toast.error('Failed to save photo.');
      return;
    }

    openLoadingModal({
      header: 'Saving photo...',
      message: 'Please wait while we save the photo',
    });

    try {
      const fileName = getChatPhotoFileName(attachmentUrl);
      const mimeType = getChatPhotoMimeType(fileName);

      if (destination.type === 'appointment') {
        const appointment = appointmentContext.appointment!;
        const normalized = await normalizeRemoteImage(
          attachmentUrl,
          fileName,
          mimeType,
        );
        const { url: hostedUrl } = await uploadBlobToPath(
          normalized.blob,
          `users/${authServiceId}/appointment_${
            destination.appointmentId
          }_${Date.now()}_${normalized.fileName}`,
          normalized.contentType,
        );

        await appendAppointmentRefImage({
          appointmentId: destination.appointmentId,
          businessId: appointment.businessId || businessId,
          status: appointment.status,
          existingRefImgsId: appointment.refImgsId,
          imageUrl: hostedUrl,
        });

        await invalidateAppointment(destination.appointmentId);
        await invalidateAppointments();

        // Calendar sidebar holds appointment state outside react-query, so mirror it here too.
        if (openedAppointment?.appointmentId === destination.appointmentId) {
          setOpenedAppointment({
            ...openedAppointment,
            refImgsId: [...(openedAppointment.refImgsId ?? []), hostedUrl],
          });
        }
        setSelectedAppointmentUpdated(true);

        toast.success('Photo saved to appointment.');
        return;
      }

      await uploadCustomerFileFromUrl({
        businessId,
        customerId: destination.customerId,
        remoteUrl: attachmentUrl,
        name: fileName,
        type: mimeType,
      });

      await invalidateCustomerOverview(destination.customerId);
      toast.success('Photo saved to customer profile.');
    } catch (error) {
      toast.error(
        isHeicUnsupportedError(error)
          ? HEIC_UNSUPPORTED_MESSAGE
          : 'Failed to save photo.',
      );
    } finally {
      closeLoadingModal();
    }
  };

  // Ref indirection keeps this callback stable so TalkJS Chatbox doesn't remount.
  const onCustomMessageAction = React.useCallback(
    (event: Talk.MessageActionEvent) => {
      saveHandlerRef.current?.(event).catch(() => undefined);
    },
    [],
  );

  return {
    onCustomMessageAction,
    appointmentContext,
  };
}

export default useSaveChatPhoto;
